import store from '@/store/index';
import router from '@/router';
import {
  extractFilenameFromHeader,
  parseApiValidationError,
  translateApiErrorMessage,
} from '@/utils';
import { captureApiError } from '@/utils/sentry';
import i18n from '@/i18n';

export const DEFAULT_REJECTION_MESSAGE = 'Request failed';

function normalizeRejectionMessage(message, fallback = DEFAULT_REJECTION_MESSAGE) {
  if (typeof message === 'string' && message) {
    return message;
  }

  if (message && typeof message.message === 'string') {
    return message.message;
  }

  if (message && typeof message.detail === 'string') {
    return message.detail;
  }

  return fallback;
}

/**
 * Configure request and response interceptors for axios instance
 * @param {Object} axiosInstance - The axios instance to configure
 */
export function setupInterceptors(axiosInstance) {
  axiosInstance.interceptors.request.use((config) => {
    // `/export` only: `exports/queue` must stay JSON (substring `export` would match `exports`)
    if (config.url.includes('/export') || config.url.includes('pdf')) {
      config.responseType = 'blob';
      // Note: Le format d'export (csv/xlsx) est passé via les paramètres de la requête
      // par les composants qui appellent l'API
    }

    if (config.url.includes('login')) {
      config.baseURL = `${process.env.VUE_APP_AUTH}`;
    }

    if (config.url.includes('translations/import')) {
      config.headers['Content-Type'] = 'multipart/form-data';
    }

    // Add authorization header if user is logged in
    if (store.state.user && !config.url.includes('login')) {
      config.headers['Authorization'] = `Bearer ${
        store.state.temporaryUser
          ? store.state.temporaryUser.token
          : store.state.user.access_token
      }`;
    }

    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => {
      // Handle email export case (status 202)
      if (response.status == 202 && response.config?.url.includes('/export')) {
        return Promise.reject({ status: 202 });
      }
      response.filename = extractFilenameFromHeader(response);
      return response;
    },

    // Error handler
    async (error) => {
      const status = error?.response?.status;
      const requestUrl = error?.config?.url;
      let message = error?.response?.data?.message || error?.response?.data || error.message;

      // Handle blob response case (errors on export routes)
      if (message instanceof Blob) {
        try {
          const text = await message.text();
          const jsonData = JSON.parse(text);
          message = jsonData.message || jsonData.error || text;
        } catch (e) {
          message = i18n.t('global.default_request_error');
        }
      }

      // Handle specific error cases
      if (status == 401) {
        store.dispatch('logout');
        if (router.currentRoute.name !== 'home') {
          router.push({ name: 'home' });
        }
        return Promise.reject(normalizeRejectionMessage(message));
      } else if (status == 403 && !error?.config?.skipForbiddenRedirect) {
        // Redirect to 403 error page
        router.push({ name: 'error-403' });
      } else if (status == 403) {
        // L'appelant gere lui-meme le 403 : pas de redirection, mais on garde Sentry
        captureApiError(error);
        return Promise.reject({
          status,
          message: normalizeRejectionMessage(message),
          fieldErrors: {},
        });
      } else if (status == 202 && requestUrl?.includes('/export')) {
        return Promise.reject({ status: 202 });
      } else {
        const validation = parseApiValidationError(error?.response?.data);
        const hasValidation =
          validation.message || Object.keys(validation.fieldErrors).length;
        const customMessages = error?.config?.errorMessages;
        let errorMessage;

        if (customMessages && customMessages[status]) {
          errorMessage = customMessages[status];
        } else if (customMessages && customMessages.fallback) {
          errorMessage = customMessages.fallback;
        } else if (hasValidation) {
          errorMessage = validation.message
            ? translateApiErrorMessage(i18n, validation.message)
            : i18n.t('global.default_request_error');
        } else {
          errorMessage = i18n.t('global.default_request_error');
          if (typeof message === 'string' && message) {
            errorMessage = `${errorMessage} : ${message}`;
          }
        }

        if (!error?.config?.skipGlobalErrorHandler) {
          store.dispatch('setNotificationMessage', {
            text: errorMessage,
            color: 'red',
          });
        }

        captureApiError(error);

        if (hasValidation) {
          return Promise.reject({
            status,
            message: validation.message,
            fieldErrors: validation.fieldErrors,
          });
        }
      }

      return Promise.reject(normalizeRejectionMessage(message));
    }
  );
}
